import React from 'react';
import Gift from 'lucide-react/dist/esm/icons/gift.js';
import Heart from 'lucide-react/dist/esm/icons/heart.js';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles.js';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right.js';
import { charityConfig } from '../../config/charity';
import InlineTranslationEdit from '../InlineTranslationEdit';
import { useContent } from '../../contexts/ContentContext';
import { useSiteConfig } from '../../contexts/SiteConfigContext';

export default function GiftBoxCharity({ editMode = false, handleTranslationUpdate }) {
  const { c } = useContent();
  const { siteDetails } = useSiteConfig() || {};
  const brandLogo = siteDetails?.siteLogo || charityConfig.logoUrl;
  const brandName = siteDetails?.siteName || charityConfig.name;
  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-violet-50 via-fuchsia-50 to-white dark:from-violet-950 dark:via-fuchsia-950 dark:to-gray-900 rounded-2xl shadow-lg border border-violet-200 dark:border-violet-800">
      <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-violet-400/20 to-fuchsia-400/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-pink-400/20 to-purple-400/20 rounded-full blur-2xl"></div>

      <div className="absolute top-3 left-6 text-yellow-400/40">
        <Sparkles className="w-5 h-5" />
      </div>
      <div className="absolute top-8 right-10 text-pink-400/40">
        <Sparkles className="w-4 h-4" />
      </div>
      <div className="absolute bottom-12 left-10 text-violet-400/40">
        <Sparkles className="w-3 h-3" />
      </div>

      <div className="relative p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-lg shadow-md">
            <Gift className="w-5 h-5 text-white" />
          </div>
          <h3 className="font-bold text-gray-900 dark:text-white"><InlineTranslationEdit translationKey="CharityGiftBoxTitle" value={c('CharityGiftBoxTitle', 'Unwrap the Impact')} fallback="Unwrap the Impact" onUpdate={handleTranslationUpdate} editMode={editMode} /></h3>
        </div>

        <div className="relative mb-4">
          <div className="relative bg-gradient-to-br from-violet-100 to-fuchsia-100 dark:from-violet-900/50 dark:to-fuchsia-900/50 rounded-xl p-1 border-2 border-violet-300 dark:border-violet-600 shadow-lg">
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-16 h-6 bg-gradient-to-r from-pink-400 via-fuchsia-500 to-violet-500 rounded-sm shadow-md"></div>
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-2 h-8 bg-gradient-to-b from-pink-500 to-fuchsia-600"></div>

            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-pink-400/50 to-fuchsia-500/50"></div>
            <div className="absolute top-1/2 left-0 transform -translate-y-1/2 w-full h-1 bg-gradient-to-r from-pink-400/50 via-fuchsia-500/50 to-violet-500/50"></div>

            <div className="relative bg-white/80 dark:bg-gray-800/80 rounded-lg p-4 m-2">
              <div className="flex items-center gap-4">
                <div className="relative flex-shrink-0">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-violet-100 to-fuchsia-100 dark:from-violet-900/50 dark:to-fuchsia-900/50 flex items-center justify-center shadow-inner border border-violet-200 dark:border-violet-700">
                    <img
                      src={brandLogo}
                      alt={brandName}
                      className="w-12 h-12 object-contain rounded-lg"
                    />
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-gradient-to-br from-pink-500 to-fuchsia-500 rounded-full flex items-center justify-center shadow-md">
                    <Heart className="w-3 h-3 text-white fill-current" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-gray-900 dark:text-white">{brandName}</h4>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{charityConfig.tagline}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-violet-100 to-fuchsia-100 dark:from-violet-900/30 dark:to-fuchsia-900/30 rounded-xl p-4 border border-violet-200 dark:border-violet-700">
          <div className="flex items-start gap-2">
            <Gift className="w-5 h-5 text-violet-500 dark:text-violet-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-violet-800 dark:text-violet-200 leading-relaxed">
              <InlineTranslationEdit translationKey="CharityGiftBoxImpact" value={c('CharityGiftBoxImpact', 'Every prize supports')} fallback="Every prize supports" onUpdate={handleTranslationUpdate} editMode={editMode} />{' '}{brandName}. {charityConfig.impactStatement}
            </p>
          </div>
        </div>

        <div className="mt-4 text-center">
          <a
            href={charityConfig.website}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm font-medium text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 transition-colors group"
          >
            <InlineTranslationEdit translationKey="CharityGiftBoxCTA" value={c('CharityGiftBoxCTA', 'Discover the gift of giving')} fallback="Discover the gift of giving" onUpdate={handleTranslationUpdate} editMode={editMode} />
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </div>
  );
}
